import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

interface UseNavigationConfirmProps {
  shouldBlock: boolean;
  message?: string;
  ignoreTabNavigation?: boolean;
}

interface NavigationConfirmReturn {
  showModal: boolean;
  confirmNavigation: () => void;
  cancelNavigation: () => void;
  modalMessage: string;
  navigateWithConfirm: (to: string) => void;
}

export const useNavigationConfirm = ({
  shouldBlock,
  message = "変更内容が失われます。このページを離れてもよろしいですか？",
  ignoreTabNavigation = false,
}: UseNavigationConfirmProps): NavigationConfirmReturn => {
  const [showModal, setShowModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(
    null
  );
  const [pendingTabClick, setPendingTabClick] = useState<HTMLElement | null>(
    null
  );
  const [isExecutingTabChange, setIsExecutingTabChange] = useState(false);
  const [skipNextConfirmation, setSkipNextConfirmation] = useState(false);
  const skipNextPopstateRef = useRef(false);
  const location = useLocation();
  const currentUrlRef = useRef(
    window.location.pathname + window.location.search + window.location.hash
  );
  const navigate = useNavigate();

  useEffect(() => {
    if (!shouldBlock) return;

    const handleClick = (e: MouseEvent | TouchEvent) => {
      if (!shouldBlock) return;

      const target = e.target as HTMLElement;
      const link = target.closest("a[href]") as HTMLAnchorElement;
      const tabButton = target.closest('[role="tab"]') as HTMLElement;

      if (
        tabButton &&
        !isExecutingTabChange &&
        !skipNextConfirmation &&
        !ignoreTabNavigation &&
        !tabButton.dataset.noConfirm
      ) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        setShowModal(true);
        setPendingNavigation("tab-change");
        setPendingTabClick(tabButton);
        return;
      }

      if (skipNextConfirmation) {
        setSkipNextConfirmation(false);
        return;
      }

      if (
        link &&
        link.href &&
        !link.href.startsWith("javascript:") &&
        !link.href.startsWith("#")
      ) {
        try {
          const url = new URL(link.href);
          if (url.origin === window.location.origin) {
            const targetPath = url.pathname;
            if (targetPath !== location.pathname) {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              setShowModal(true);
              setPendingNavigation(targetPath);
              return;
            }
          }
        } catch (error) {
          console.error("Error parsing URL:", error);
        }
      }
    };

    // キャプチャフェーズでリッスンし、<a>タグクリックを検知して遷移を止めてモーダル表示する。
    // キャプチャにすることで、React RouterのLink(navigate)より先にpreventDefaultが走り、遷移をブロックできる。
    document.addEventListener("click", handleClick, true);
    document.addEventListener("touchend", handleClick, true);

    return () => {
      document.removeEventListener("click", handleClick, true);
      document.removeEventListener("touchend", handleClick, true);
    };
  }, [shouldBlock, location.pathname, isExecutingTabChange, skipNextConfirmation, ignoreTabNavigation]);

  useEffect(() => {
    if (!shouldBlock) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = message;
      return message;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [shouldBlock, message]);

  useEffect(() => {
    currentUrlRef.current =
      window.location.pathname + window.location.search + window.location.hash;
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (!shouldBlock) return;

    const handlePopState = (_event: PopStateEvent) => {
      if (skipNextPopstateRef.current) {
        skipNextPopstateRef.current = false;
        return;
      }

      window.history.pushState(null, "", window.location.href);
      setShowModal(true);
      setPendingNavigation("back");
    };

    // 戻る (popstate) を捕まえて確認モーダルを出すために番兵エントリを 1 段積む。
    // shouldBlock が false に戻る or 同 URL のまま unmount された場合は cleanup で
    // この番兵を掃除しないと、ユーザーが「戻る」を 2 回押さないとページを離れられなくなる。
    const sentinelUrl =
      window.location.pathname +
      window.location.search +
      window.location.hash;
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      // 既に別 URL に遷移済み (= ページを離れた) の場合は触らない。
      // 同 URL のままなら shouldBlock=false への遷移なので番兵を掃除する。
      // pop 後 location.state がリセットされうるが、これは各ページ側で
      // state を deps とする useEffect で再構築すればよい。
      const currentUrl =
        window.location.pathname +
        window.location.search +
        window.location.hash;
      if (currentUrl === sentinelUrl) {
        skipNextPopstateRef.current = true;
        window.history.back();
      }
    };
  }, [shouldBlock]);

  const confirmNavigation = useCallback(() => {
    setShowModal(false);

    if (pendingNavigation === "back") {
      skipNextPopstateRef.current = true;
      window.history.go(-2);
    } else if (pendingNavigation === "tab-change" && pendingTabClick) {
      setSkipNextConfirmation(true);
      setIsExecutingTabChange(true);
      setPendingTabClick(null);
      pendingTabClick.click();
      setTimeout(() => {
        setIsExecutingTabChange(false);
      }, 100);
    } else if (pendingNavigation) {
      navigate(pendingNavigation);
    }

    setPendingNavigation(null);
  }, [pendingNavigation, navigate, pendingTabClick]);

  const cancelNavigation = useCallback(() => {
    setShowModal(false);
    setPendingNavigation(null);
    setPendingTabClick(null);
    setIsExecutingTabChange(false);
    setSkipNextConfirmation(false);
  }, []);

  const navigateWithConfirm = useCallback(
    (to: string) => {
      if (shouldBlock) {
        setShowModal(true);
        setPendingNavigation(to);
      } else {
        navigate(to);
      }
    },
    [shouldBlock, navigate]
  );

  return {
    showModal,
    confirmNavigation,
    cancelNavigation,
    modalMessage: message,
    navigateWithConfirm,
  };
};

